import { compareContentRowsByOrder } from '../utils/scheduleContentMatcher';
import React from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useSearchParams } from 'react-router-dom';
import { projectIdFromProgramPath } from '../utils/programRouteParams';
import { useProgramProgressSavedRefresh } from '../hooks/useProgramProgressSavedRefresh';
import { useProgramPostSaveDisplayHold, cloneProgramTableHoldPayload } from '../hooks/useProgramPostSaveDisplayHold';
import { useReleaseTrappedKeepAliveFocusWhenInactive } from '../utils/keepAliveFocusRelease';
import { ProgramProgressEmptyStateTableCard } from '../components/program/ProgramProgressEmptyStateTableCard';
import { useProgramSidebar } from '../contexts/ProgramSidebarContext';
import { useCalendarLinkedAcademicYear } from '../hooks/useCalendarLinkedAcademicYear';
import { useKeepAliveProgramTerm } from '../hooks/useKeepAliveProgramTerm';
import ProgramProgressTable, { type ProgramProgressUndoRedoApi } from '../components/program/ProgramProgressTable';
import ProgramImportLearningContentModal from '../components/program/ProgramImportLearningContentModal';
import ProgramExportLearningContentModal from '../components/program/ProgramExportLearningContentModal';
import ProgramLearningContentActionSelectModal from '../components/program/ProgramLearningContentActionSelectModal';
import ProgramTableDocumentExportButtons from '../components/program/ProgramTableDocumentExportButtons';
import ProgramImportLearningDisabledHint from '../components/program/ProgramImportLearningDisabledHint';
import ProgramWorkspaceStaleConflictBanner from '../components/program/ProgramWorkspaceStaleConflictBanner';
import ProgramToolbarUndoRedoButtons from '../components/program/ProgramToolbarUndoRedoButtons';
import { GradeTrackLeaveContext } from '../contexts/GradeTrackLeaveContext';
import { useAnnualProjectProgramsResolved } from '../hooks/useAnnualProjectProgramsResolved';
import { useProgramProgressData } from '../hooks/useProgramProgressData';
import { useProgramScopedContentRows } from '../hooks/useProgramScopedContentRows';
import { useProgramWorkspaceScopeServerSync } from '../hooks/useProgramWorkspaceScopeServerSync';
import {
  AnnualProjectProgram,
  normalizeHexColorForProjectCompare,
} from '../utils/annualProjectProgramsStorage';
import { parseGradeDisplayName } from '../utils/gradeDisplayName';
import { AdminLearningContentRow, ProgramFixedContentRowUpsert, ProgramProgressCell } from '../types/domain';
import {
  buildProgramProgressRows,
  buildTrackNameByTimetableTextByGrade,
  buildWeeksFromRange,
  resolveClassEntryByGradeId,
} from '../utils/programProgressRows';
import { buildGradeTrackTimetableContext } from '../utils/trackTimetableAlias';
import {
  ensureProgramWorkspaceScopeSnapshot,
  isProgramWorkspaceScopeDirty,
  getProgramWorkspaceScopeEntry,
  PROGRAM_WORKSPACE_CHANGED_EVENT,
  pruneProgramWorkspaceScopes,
  registerDiscardDirtyWorkspaceOnPageHide,
  removeProgramWorkspaceScopeEntry,
  resolveProgramWorkspacePersistKind,
  upsertProgramWorkspaceScopeEntry,
  type ProgramWorkspaceRowsChangeMeta,
} from '../utils/programEditWorkspace';
import {
  computeProgramSavePending,
  discardAllProgramFamilyLeaveDrafts,
  saveProgramGradeTermWorkspaceAndAttachments,
} from '../utils/programGradeTermSave';
import {
  PROGRAM_CONTENT_SLIDE_DRAFT_PREFIX,
  PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT,
} from '../services/programContentSlideService';
import {
  LEARNING_WORKSHEET_PENDING_PREFIX,
  LEARNING_WORKSHEET_PENDING_CHANGED_EVENT,
} from '../utils/learningContentWorksheetPendingStorage';
import { buildAnnualCellKey } from '../utils/annualTimetable';
import {
  alignProgramContentRowsToScheduleSlotLayout,
  buildProjectFixedWorkspaceBaselineUpserts,
  isPristineMinimumProgramContentPlaceholderRows,
  programFixedWorkspaceRowsDigest,
  trimTrailingEmptyProgramContentOverflowRows,
  withMinimumProgramContentRowsForDisplay,
} from '../utils/programWorkspaceBaselineDigest';
import { mergeProgramWorkspaceUpsertsIntoBaselineRows } from '../utils/programWorkspaceDisplayMerge';
import { readValidScopedContentRowsFromLocalCache } from '../utils/programScopedContentLsBridge';
import { pickProgramScopedRowsForDisplay } from '../utils/programScopedRowsDisplayPick';
import { getAnnualTimetableCells, upsertAnnualTimetableCells } from '../services/annualTimetableService';
import { ensureProjectProgramScopedContentRows, fetchProgramScopedContentRowsHead } from '../services/programScopedContentService';
import { emitAnnualTimetableSaved } from '../utils/annualTimetableSavedEvents';
import { buildWildcardCharsByGrade } from '../utils/trackWildcard';
import {
  mergeProgramBaselineWithAdminLearningContent,
  exportProgramLearningToWorkspace,
  programAlignedImportColumnsHaveData,
  PROGRAM_IMPORT_LEARNING_CONTENT_BLOCKED_MESSAGE,
} from '../utils/programLearningContentImport';
import { resolveProgramImportLearningDisabledHint } from '../utils/programImportLearningDisabledHint';
import {
  createProgramLearningContentCsv,
  parseProgramLearningContentCsv,
} from '../utils/programLearningContentCsv';
import {
  buildProgramLearningContentExportFileBase,
  buildProgramDocumentScopeLabel,
  buildProgramNamedDocumentTitle,
  useProgramLearningContentDocumentExport,
} from '../hooks/useProgramLearningContentDocumentExport';

type ProgramProjectPageProps = {
  isActive?: boolean;
};

/** 프로젝트 프로그램 — 선택 프로젝트 색으로 연간진도표 일정 생성 */
const ProgramProjectPage: React.FC<ProgramProjectPageProps> = ({ isActive = true }) => {
  const location = useLocation();
  useReleaseTrappedKeepAliveFocusWhenInactive(isActive);
  const normalizeHexColor = React.useCallback((raw: string) => {
    const v = (raw ?? '').trim();
    if (!v) return '';
    if (v.startsWith('#')) return v;
    return `#${v}`;
  }, []);
  const [searchParams] = useSearchParams();
  const lastActiveProjectIdRef = React.useRef('');
  const projectIdParam = React.useMemo(() => {
    if (!isActive) return lastActiveProjectIdRef.current;
    const fromPath = projectIdFromProgramPath(location.pathname);
    if (fromPath) lastActiveProjectIdRef.current = fromPath;
    return fromPath || lastActiveProjectIdRef.current;
  }, [isActive, location.pathname]);
  const {
    programFocusGradeId,
    grades,
    workspaceId,
    setProgramFocusGradeId,
  } = useProgramSidebar();
  const gradeIdFromUrl = searchParams.get('gradeId');
  const focusAnnualCellKeyFromUrl = searchParams.get('focusAnnualCellKey')?.trim() ?? '';
  const effectiveGradeId = gradeIdFromUrl || programFocusGradeId || '';
  const programTermFromUrl = searchParams.get('programTerm');
  const termNumber = useKeepAliveProgramTerm(isActive, programTermFromUrl);
  const academicYear = useCalendarLinkedAcademicYear(workspaceId, effectiveGradeId.trim());
  const [workspaceStateRev, setWorkspaceStateRev] = React.useState(0);
  const [saveRefreshRev, setSaveRefreshRev] = React.useState(0);
  const bumpSaveRefreshRev = React.useCallback(() => setSaveRefreshRev((v) => v + 1), []);
  const bumpProgramSavePendingRevs = React.useCallback(() => {
    bumpSaveRefreshRev();
    setSlideDraftDirtyRev((v) => v + 1);
    setWorksheetDraftDirtyRev((v) => v + 1);
    setWorkspaceStateRev((v) => v + 1);
  }, [bumpSaveRefreshRev]);
  useProgramProgressSavedRefresh({
    workspaceId,
    gradeId: effectiveGradeId,
    academicYear,
    termNumber,
    onRefresh: bumpProgramSavePendingRevs,
  });
  const [programTableMountKey, setProgramTableMountKey] = React.useState(0);
  const [importLearningModalOpen, setImportLearningModalOpen] = React.useState(false);
  const [exportLearningModalOpen, setExportLearningModalOpen] = React.useState(false);
  const [importMethodSelectOpen, setImportMethodSelectOpen] = React.useState(false);
  const [exportMethodSelectOpen, setExportMethodSelectOpen] = React.useState(false);
  const [importLearningApplying, setImportLearningApplying] = React.useState(false);
  const [exportLearningApplying, setExportLearningApplying] = React.useState(false);
  const importCsvInputRef = React.useRef<HTMLInputElement | null>(null);
  const programUndoRedoApiRef = React.useRef<ProgramProgressUndoRedoApi | null>(null);
  const [programUndoRedoAvail, setProgramUndoRedoAvail] = React.useState({ canUndo: false, canRedo: false });
  const projectTimetableSyncApiRef = React.useRef<{
    importFromAnnual: () => void;
    exportToAnnual: () => void;
  } | null>(null);
  const [projectTimetableSyncReady, setProjectTimetableSyncReady] = React.useState(false);
  const [projectTimetableSyncBusy, setProjectTimetableSyncBusy] = React.useState(false);
  const [projectTimetableSyncConfirm, setProjectTimetableSyncConfirm] = React.useState<
    null | 'import' | 'export'
  >(null);
  const handleBindProjectTimetableSyncActions = React.useCallback(
    (
      api: {
        importFromAnnual: () => void;
        exportToAnnual: () => void;
        busy: boolean;
      } | null
    ) => {
      if (!api) {
        projectTimetableSyncApiRef.current = null;
        setProjectTimetableSyncReady(false);
        setProjectTimetableSyncBusy(false);
        return;
      }
      projectTimetableSyncApiRef.current = {
        importFromAnnual: api.importFromAnnual,
        exportToAnnual: api.exportToAnnual,
      };
      setProjectTimetableSyncReady(true);
      setProjectTimetableSyncBusy((prev) => (prev === api.busy ? prev : api.busy));
    },
    []
  );

  React.useEffect(() => {
    if (effectiveGradeId) setProgramFocusGradeId(effectiveGradeId);
  }, [effectiveGradeId, setProgramFocusGradeId]);

  const gradeRow = React.useMemo(
    () => (effectiveGradeId ? grades.find((g) => g.id === effectiveGradeId) ?? null : null),
    [grades, effectiveGradeId]
  );
  const classParsed = React.useMemo(
    () => (gradeRow ? parseGradeDisplayName(gradeRow.name) : null),
    [gradeRow]
  );
  const defaultGradeNumberForImport = React.useMemo((): 1 | 2 | 3 | 4 | 5 | 6 => {
    const g = classParsed?.grades?.[0];
    if (typeof g === 'number' && g >= 1 && g <= 6) return g as 1 | 2 | 3 | 4 | 5 | 6;
    return 1;
  }, [classParsed]);

  const projectPrograms = useAnnualProjectProgramsResolved(workspaceId, effectiveGradeId, academicYear);
  const project = React.useMemo<AnnualProjectProgram | null>(() => {
    if (!projectIdParam) return null;
    return projectPrograms.find((p) => p.id === projectIdParam) ?? null;
  }, [projectIdParam, projectPrograms]);

  const {
    loading,
    error,
    terms,
    schedule,
    tracks,
    gradeTrackSections,
    regularColors,
    dedicatedColors,
    baseExcludeColors,
    holidaySet,
    cellsByKey,
    schoolScopedCacheSyncReady,
  } = useProgramProgressData({
    workspaceId,
    grades,
    gradeId: effectiveGradeId,
    academicYear,
    termNumber: termNumber ?? 1,
    isActive,
  });
  const dataActive = isActive && schoolScopedCacheSyncReady;
  const selectedEntry = React.useMemo(
    () => resolveClassEntryByGradeId(schedule, grades, effectiveGradeId),
    [effectiveGradeId, grades, schedule]
  );
  const termDef = React.useMemo(
    () => (termNumber ? terms.find((term) => term.term_number === termNumber) ?? null : null),
    [termNumber, terms]
  );
  const weeks = React.useMemo(
    () => buildWeeksFromRange(termDef?.starts_on ?? '', termDef?.ends_on ?? ''),
    [termDef?.starts_on, termDef?.ends_on]
  );
  const trackNameByTimetableTextByGrade = React.useMemo(
    () => buildTrackNameByTimetableTextByGrade(tracks),
    [tracks]
  );
  const gradeTrackTimetableContext = React.useMemo(
    () => buildGradeTrackTimetableContext(workspaceId, effectiveGradeId, tracks, gradeTrackSections),
    [workspaceId, effectiveGradeId, tracks, gradeTrackSections]
  );
  const rows = React.useMemo(() => {
    if (!selectedEntry || !termDef || !termNumber || !project) return [];
    const gradeRawName = grades.find((g) => g.id === selectedEntry.gradeId)?.name ?? selectedEntry.label;
    const projectColorNorm = normalizeHexColorForProjectCompare(project.colorHex);
    const baseRows = buildProgramProgressRows({
      workspaceId,
      academicYear,
      termNumber,
      selectedEntry,
      gradeRawName,
      termDef,
      weeks,
      holidaySet,
      cellsByKey,
      dedicatedColors,
      baseExcludeColors,
      regularColors,
      annualProjectPrograms: projectPrograms,
      trackNameByTimetableTextByGrade,
      gradeTrackTimetableContext,
      rowFilter: (input) => {
        const { timetableColorHex, isProjectProgram } = input;
        return (
          isProjectProgram &&
          projectColorNorm !== null &&
          normalizeHexColorForProjectCompare(timetableColorHex) === projectColorNorm
        );
      },
    });
    // 프로젝트 하위 페이지의 시간표 열은 항상 해당 프로젝트 색으로 표시
    const displayColor = normalizeHexColor(project.colorHex);
    return baseRows.map((row) => ({ ...row, timetableColorHex: displayColor }));
  }, [
    academicYear,
    cellsByKey,
    dedicatedColors,
    baseExcludeColors,
    grades,
    holidaySet,
    project,
    projectPrograms,
    regularColors,
    workspaceId,
    selectedEntry,
    termDef,
    termNumber,
    trackNameByTimetableTextByGrade,
    gradeTrackTimetableContext,
    normalizeHexColor,
    weeks,
  ]);
  const registeredTimetableNames = React.useMemo(() => {
    const set = new Set<string>();
    tracks.forEach((track) => {
      if ((track.grade_id ?? '').trim() !== effectiveGradeId) return;
      const base = (track.name ?? '').trim();
      const alias = (track.description ?? '').trim();
      if (base) set.add(base);
      if (alias) set.add(alias);
    });
    return Array.from(set);
  }, [effectiveGradeId, tracks]);
  const registeredTimetableWildcardChars = React.useMemo(() => {
    const byGrade = buildWildcardCharsByGrade(gradeTrackSections);
    return Array.from(byGrade[effectiveGradeId] ?? []);
  }, [effectiveGradeId, gradeTrackSections]);
  const scheduleSlotAnnualCellKeys = React.useMemo(() => rows.map((r) => r.annualCellKey), [rows]);

  const titlePrefix = classParsed ? `${classParsed.title} — ` : '';
  void workspaceStateRev;

  const prevProgramTermRef = React.useRef<1 | 2 | null>(null);
  React.useEffect(() => {
    if (termNumber == null) {
      prevProgramTermRef.current = null;
      return;
    }
    if (prevProgramTermRef.current != null && prevProgramTermRef.current !== termNumber) {
      setProgramTableMountKey((k) => k + 1);
      setWorkspaceStateRev((v) => v + 1);
    }
    prevProgramTermRef.current = termNumber;
  }, [termNumber]);

  const scopeKey = projectIdParam ?? '';
  const projectScopeId = React.useMemo(
    () => `project|${workspaceId}|${effectiveGradeId}|${academicYear}|${termNumber}|${scopeKey}`,
    [academicYear, effectiveGradeId, workspaceId, scopeKey, termNumber]
  );
  const activeWorkspaceScopeId = React.useMemo(
    () => (workspaceId && effectiveGradeId && termNumber && scopeKey ? projectScopeId : ''),
    [effectiveGradeId, projectScopeId, workspaceId, scopeKey, termNumber]
  );
  const projectWorkspaceEntry = getProgramWorkspaceScopeEntry(projectScopeId);
  const {
    loading: scopedRowsLoading,
    rows: scopedContentRows,
    saveSnapshotRows: saveScopedSnapshotRows,
  } =
    useProgramScopedContentRows({
      workspaceId,
      academicYear,
      termNumber,
      gradeId: effectiveGradeId,
      scopeType: 'project',
      scopeKey,
      refreshKey: saveRefreshRev,
      isActive: dataActive,
    });
  const projectScopedBootstrapRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    projectScopedBootstrapRef.current = null;
  }, [projectScopeId]);
  React.useEffect(() => {
    if (!dataActive) return;
    if (!workspaceId || !effectiveGradeId || !termNumber || !scopeKey || scopedRowsLoading) return;
    if (scopedContentRows.length > 0) return;
    /** 일정 슬롯이 없으면 ensure 생략 — 가상 1행만 두고 첫 저장 때 실제 행을 만든다 */
    if (rows.length === 0) return;
    const bootstrapKey = `${projectScopeId}|${rows.length}`;
    if (projectScopedBootstrapRef.current === bootstrapKey) return;
    projectScopedBootstrapRef.current = bootstrapKey;
    let cancelled = false;
    void ensureProjectProgramScopedContentRows({
      workspaceId,
      gradeId: effectiveGradeId,
      academicYear,
      termNumber,
      projectId: scopeKey,
      scheduleAnnualCellKeys: rows.map((row) => row.annualCellKey),
    })
      .then((saved) => {
        if (cancelled || saved.length === 0) return;
        setSaveRefreshRev((v) => v + 1);
      })
      .catch(() => {
        projectScopedBootstrapRef.current = null;
      });
    return () => {
      cancelled = true;
    };
  }, [
    academicYear,
    effectiveGradeId,
    projectScopeId,
    rows,
    scopeKey,
    scopedContentRows.length,
    scopedRowsLoading,
    termNumber,
    workspaceId,
    dataActive,
  ]);
  const scopedRowsFromCache = React.useMemo(() => {
    if (!workspaceId || !effectiveGradeId || !termNumber || !scopeKey) return [];
    return readValidScopedContentRowsFromLocalCache(
      workspaceId,
      academicYear,
      termNumber,
      effectiveGradeId,
      'project',
      scopeKey
    ).rows;
  }, [
    academicYear,
    effectiveGradeId,
    saveRefreshRev,
    scopeKey,
    termNumber,
    workspaceId,
    workspaceStateRev,
  ]);
  const rowsForDisplayScope = React.useMemo(
    () => pickProgramScopedRowsForDisplay(scopedContentRows, scopedRowsFromCache),
    [scopedContentRows, scopedRowsFromCache]
  );
  const baselineWorkspaceUpserts = React.useMemo((): ProgramFixedContentRowUpsert[] => {
    if (!effectiveGradeId || !termNumber || !workspaceId || !scopeKey) return [];
    const scopedSorted = [...rowsForDisplayScope].sort(compareContentRowsByOrder);
    return buildProjectFixedWorkspaceBaselineUpserts({
      workspaceId,
      academicYear,
      termNumber,
      gradeId: effectiveGradeId,
      rows,
      scopedRowsSorted: scopedSorted,
    });
  }, [academicYear, effectiveGradeId, rows, workspaceId, scopeKey, rowsForDisplayScope, termNumber]);
  /** 저장 후 dirty 비교: 프로젝트 일정 행보다 긴 표(넘침)와 길이를 맞춘다(scoped DB만 사용). */
  const persistedAlignedWorkspaceUpserts = React.useMemo((): ProgramFixedContentRowUpsert[] => {
    if (!effectiveGradeId || !termNumber || !workspaceId || !scopeKey) return [];
    const scopedSorted = [...rowsForDisplayScope].sort(compareContentRowsByOrder);
    return alignProgramContentRowsToScheduleSlotLayout({
      contentRowsSorted: scopedSorted,
      baselineRows: baselineWorkspaceUpserts,
    });
  }, [baselineWorkspaceUpserts, effectiveGradeId, rowsForDisplayScope, workspaceId, scopeKey, termNumber]);
  const tableLoading = loading || scopedRowsLoading;
  const effectiveScopedRows = React.useMemo(() => {
    if (!projectWorkspaceEntry?.isDirty || !projectWorkspaceEntry.rows?.length) return rowsForDisplayScope;
    const baselineSorted = [...rowsForDisplayScope].sort(compareContentRowsByOrder);
    const merged = mergeProgramWorkspaceUpsertsIntoBaselineRows(baselineSorted, projectWorkspaceEntry.rows, {
      persistKind: projectWorkspaceEntry.persistKind === 'replace' ? 'replace' : 'patch',
      identity: 'id-position',
    });
    return merged.map((row, idx) => ({ ...row, id: row.id ?? `virtual-${projectScopeId}-${idx}` }));
  }, [
    projectScopeId,
    projectWorkspaceEntry?.isDirty,
    projectWorkspaceEntry?.rows,
    projectWorkspaceEntry?.persistKind,
    rowsForDisplayScope,
    workspaceStateRev,
  ]);
  const alignedContentRows = React.useMemo(() => {
    const sorted = [...effectiveScopedRows].sort(compareContentRowsByOrder);
    return alignProgramContentRowsToScheduleSlotLayout({
      contentRowsSorted: sorted,
      baselineRows: baselineWorkspaceUpserts,
    });
  }, [baselineWorkspaceUpserts, effectiveScopedRows]);
  /** 프로젝트 일정 행이 없을 때만 최소 빈 1행(미리 편집용) */
  const contentRowsForProgramTable = React.useMemo(() => {
    let result =
      rows.length > 0
        ? alignedContentRows
        : !workspaceId || !effectiveGradeId || !termNumber || !scopeKey
          ? alignedContentRows
          : withMinimumProgramContentRowsForDisplay(alignedContentRows, {
              workspaceId,
              gradeId: effectiveGradeId,
              academicYear,
              termNumber,
            });
    /** dirty여도 DB/캐시 잔여 빈 넘침 행은 숨김(temp- 수업추가 행만 trim이 보존) */
    result = trimTrailingEmptyProgramContentOverflowRows(result);
    return result;
  }, [
    academicYear,
    alignedContentRows,
    effectiveGradeId,
    rows.length,
    scopeKey,
    termNumber,
    workspaceId,
  ]);
  const programImportLearningBlocked = React.useMemo(
    () => programAlignedImportColumnsHaveData(alignedContentRows),
    [alignedContentRows]
  );
  const { tableRows, programCellsByAnnualKey } = React.useMemo(() => {
    const mergedRows: typeof rows = [];
    const cellMap: Record<string, ProgramProgressCell> = {};
    const projectBg = project ? normalizeHexColor(project.colorHex) : '';

    contentRowsForProgramTable.forEach((contentRow, orderIndex) => {
      const schedule = orderIndex < rows.length ? rows[orderIndex] : undefined;
      const annualCellKey = schedule ? schedule.annualCellKey : `orphan:${contentRow.id ?? orderIndex}`;
      /** 시간표 열은 프로그램 내용 값만 쓴다. 연간 셀 text로 빈칸을 채우지 않는다. */
      const contentTimetableText = contentRow.timetable_text ?? '';
      const row = schedule
        ? {
            ...schedule,
            timetableText: contentTimetableText,
            timetableColorHex: projectBg || schedule.timetableColorHex,
            fixedContentId: contentRow.id,
            fixedOrderIndex: orderIndex,
            fixedSourceAnnualCellKey: schedule.annualCellKey,
            isScheduleAvailable: true,
            isScheduleSlotOverflow: false,
          }
        : {
            annualCellKey,
            gradeId: effectiveGradeId,
            mondayIso: '',
            weekdayKey: 'mon' as const,
            slotIndex: orderIndex,
            seq: 0,
            dateLabel: '',
            weekdayLabel: '',
            periodLabel: '',
            gradeLabel: '',
            semesterLabel: '',
            templateLabel: '',
            timetableText: contentTimetableText,
            timetableColorHex: projectBg,
            timetableTextShrink: false,
            timetableSlotKind: 'non_class' as const,
            programKindLabel: '',
            memo: '',
            isScheduleAvailable: false,
            isScheduleSlotOverflow: true,
            fixedContentId: contentRow.id,
            fixedOrderIndex: orderIndex,
            fixedSourceAnnualCellKey: undefined,
          };
      mergedRows.push(row);
      cellMap[annualCellKey] = {
        id: contentRow.id ?? '',
        workspace_id: workspaceId,
        grade_id: effectiveGradeId,
        academic_year: academicYear,
        term_number: termNumber ?? 1,
        monday_iso: schedule?.mondayIso ?? '',
        weekday_key: schedule?.weekdayKey ?? 'mon',
        slot_index: schedule?.slotIndex ?? orderIndex,
        timetable_text: contentTimetableText,
        unit_text: contentRow.unit_text ?? '',
        learning_text: contentRow.learning_text ?? '',
        learning_note: contentRow.learning_note ?? '',
        presentation_ref_id: contentRow.presentation_ref_id ?? null,
        worksheet_ref_id: contentRow.worksheet_ref_id ?? null,
        page_text: contentRow.page_text ?? '',
        assistant_page_text: contentRow.assistant_page_text ?? '',
        current_period_text: contentRow.current_period_text ?? '',
        total_period_text: contentRow.total_period_text ?? '',
        material_text: contentRow.material_text ?? '',
        record_cross_curricular_text: contentRow.record_cross_curricular_text ?? '',
        record_memo_text: contentRow.record_memo_text ?? '',
        record_evaluation_text: contentRow.record_evaluation_text ?? '',
        record_class_text: contentRow.record_class_text ?? '',
        bg_color_by_field: contentRow.bg_color_by_field ?? {},
      };
    });
    return { tableRows: mergedRows, programCellsByAnnualKey: cellMap };
  }, [
    academicYear,
    contentRowsForProgramTable,
    effectiveGradeId,
    normalizeHexColor,
    project,
    rows,
    workspaceId,
    termNumber,
  ]);
  const liveProgramTableDisplay = React.useMemo(
    () => ({ tableRows, programCellsByAnnualKey }),
    [programCellsByAnnualKey, tableRows]
  );
  const {
    display: heldProgramTableDisplay,
    holding: postSaveHolding,
    beginHold,
    endHold,
  } = useProgramPostSaveDisplayHold(liveProgramTableDisplay);
  const displayTableRows = heldProgramTableDisplay.tableRows;
  const displayProgramCellsByAnnualKey = heldProgramTableDisplay.programCellsByAnnualKey;
  const showTableLoading = tableLoading && !postSaveHolding;

  const projectDocumentExportTitle = React.useMemo(
    () => buildProgramNamedDocumentTitle((project?.name ?? '').trim() || '프로젝트'),
    [project?.name]
  );

  const projectDocumentScopeLabel = React.useMemo(
    () => buildProgramDocumentScopeLabel(termNumber ?? 1),
    [termNumber]
  );

  const projectDocumentExportFileBase = React.useMemo(
    () =>
      buildProgramLearningContentExportFileBase({
        scopeLabel: classParsed?.title ?? '클래스',
        programLabel: (project?.name ?? '').trim() || '프로젝트',
        termNumber: termNumber ?? 1,
      }),
    [classParsed?.title, project?.name, termNumber]
  );

  const {
    canExport: canExportProgramDocument,
    exportBusy: programDocumentExportBusy,
    handleSaveExcel: handleProgramDocumentSaveExcel,
    handleSavePdf: handleProgramDocumentSavePdf,
    handlePrint: handleProgramDocumentPrint,
  } = useProgramLearningContentDocumentExport({
    tableRows,
    programCellsByAnnualKey,
    scopeLabel: projectDocumentScopeLabel,
    programTitle: projectDocumentExportTitle,
    scopeBadge: termNumber === 2 ? 'term2' : 'term1',
    fileNameBase: projectDocumentExportFileBase,
  });

  const upsertScopeContentRows = React.useCallback(
    async (rowsToSave: ProgramFixedContentRowUpsert[]) => {
      if (!effectiveGradeId || !termNumber || !scopeKey) return;
      const snapshot = rowsToSave.map((row, orderIndex) => {
        const rawId = (row.id ?? '').trim();
        const id =
          rawId.startsWith('virtual-') || rawId.startsWith('extra:') || rawId.startsWith('temp-')
            ? undefined
            : rawId || undefined;
        return {
          id,
          workspace_id: workspaceId,
          grade_id: effectiveGradeId,
          academic_year: academicYear,
          term_number: termNumber,
          scope_type: 'project' as const,
          scope_key: scopeKey,
          /** 행 순서 = 표에서의 위치(0부터). 연간칸 키로 짝짓지 않는다 */
          order_index: orderIndex,
          source_annual_cell_key: null,
          timetable_text: row.timetable_text ?? '',
          unit_text: row.unit_text ?? '',
          learning_text: row.learning_text ?? '',
          learning_note: row.learning_note ?? '',
          presentation_ref_id: row.presentation_ref_id ?? null,
          worksheet_ref_id: row.worksheet_ref_id ?? null,
          page_text: row.page_text ?? '',
          assistant_page_text: row.assistant_page_text ?? '',
          current_period_text: row.current_period_text ?? '',
          total_period_text: row.total_period_text ?? '',
          material_text: row.material_text ?? '',
          record_cross_curricular_text: row.record_cross_curricular_text ?? '',
          record_memo_text: row.record_memo_text ?? '',
          record_evaluation_text: row.record_evaluation_text ?? '',
          record_class_text: row.record_class_text ?? '',
          record_member_group_id: row.record_member_group_id,
          bg_color_by_field: row.bg_color_by_field ?? {},
        };
      });
      await saveScopedSnapshotRows(snapshot, rowsForDisplayScope);
    },
    [
      academicYear,
      effectiveGradeId,
      rowsForDisplayScope,
      saveScopedSnapshotRows,
      workspaceId,
      scopeKey,
      termNumber,
    ]
  );
  const handleApplyProgramLearningImport = React.useCallback(
    async (adminRows: AdminLearningContentRow[]) => {
      if (!workspaceId || !effectiveGradeId || !termNumber || !scopeKey) return;
      if (programAlignedImportColumnsHaveData(alignedContentRows)) return;
      setImportLearningApplying(true);
      try {
        // 프로젝트 일정이 없어도 빈 표에 학습내용 행을 구성할 수 있게 seedMeta 전달
        const merged = mergeProgramBaselineWithAdminLearningContent(alignedContentRows, adminRows, {
          workspaceId,
          gradeId: effectiveGradeId,
          academicYear,
          termNumber,
        });
        const baseline = persistedAlignedWorkspaceUpserts;
        const dirty =
          baseline.length !== merged.length ||
          programFixedWorkspaceRowsDigest(merged) !== programFixedWorkspaceRowsDigest(baseline);
        if (!dirty) {
          setImportLearningModalOpen(false);
          return;
        }
        upsertProgramWorkspaceScopeEntry({
          scopeId: projectScopeId,
          scopeType: 'project',
          scopeKey,
          workspaceId,
          gradeId: effectiveGradeId,
          academicYear,
          termNumber,
          rows: merged,
          isDirty: true,
          updatedAt: Date.now(),
          persistKind: resolveProgramWorkspacePersistKind(undefined, merged.length, baseline.length),
        });
        setImportLearningModalOpen(false);
      } finally {
        setImportLearningApplying(false);
      }
    },
    [
      academicYear,
      alignedContentRows,
      effectiveGradeId,
      persistedAlignedWorkspaceUpserts,
      projectScopeId,
      scopeKey,
      termNumber,
      workspaceId,
    ]
  );
  const exportLearningTemplateName = React.useMemo(
    () => (project?.name ?? '').trim() || '프로젝트',
    [project?.name]
  );
  const handleExportProgramLearning = React.useCallback(async () => {
    if (!workspaceId || !termNumber || !scopeKey) return;
    setExportLearningApplying(true);
    try {
      const subCategory = termNumber === 1 ? 'term1' : 'term2';
      const result = await exportProgramLearningToWorkspace({
        workspaceId,
        gradeId: effectiveGradeId,
        gradeNumber: defaultGradeNumberForImport,
        subCategory,
        templateName: exportLearningTemplateName,
        programRows: alignedContentRows,
      });
      setExportLearningModalOpen(false);
      setSaveToastState({
        kind: 'success',
        message: result.replacedPriorExport
          ? `기본 학습내용 등록 "${exportLearningTemplateName}" 편제의 기존 내보내기 데이터를 대체했습니다.`
          : `기본 학습내용 등록 "${exportLearningTemplateName}" 편제로 내보냈습니다.`,
      });
    } catch (e) {
      setSaveToastState({
        kind: 'error',
        message: e instanceof Error ? e.message : '학습내용 내보내기에 실패했습니다.',
      });
    } finally {
      setExportLearningApplying(false);
    }
  }, [
    alignedContentRows,
    defaultGradeNumberForImport,
    effectiveGradeId,
    exportLearningTemplateName,
    scopeKey,
    termNumber,
    workspaceId,
  ]);
  const handleImportCsvFileSelected = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.currentTarget.value = '';
      if (!file || !termNumber) return;
      if (programAlignedImportColumnsHaveData(alignedContentRows)) {
        setSaveToastState({
          kind: 'error',
          message: PROGRAM_IMPORT_LEARNING_CONTENT_BLOCKED_MESSAGE,
        });
        return;
      }
      setImportLearningApplying(true);
      try {
        const text = await file.text();
        const subCategory = termNumber === 1 ? 'term1' : 'term2';
        const parsedRows = parseProgramLearningContentCsv(text, defaultGradeNumberForImport, subCategory);
        if (parsedRows.length === 0) {
          setSaveToastState({
            kind: 'error',
            message: 'CSV 파일에 가져올 학습내용이 없습니다.',
          });
          return;
        }
        await handleApplyProgramLearningImport(parsedRows);
        setSaveToastState({
          kind: 'success',
          message: `CSV에서 ${parsedRows.length}개 행을 가져왔습니다.`,
        });
      } catch (error) {
        setSaveToastState({
          kind: 'error',
          message: error instanceof Error ? error.message : 'CSV 파일 가져오기에 실패했습니다.',
        });
      } finally {
        setImportLearningApplying(false);
      }
    },
    [
      alignedContentRows,
      defaultGradeNumberForImport,
      handleApplyProgramLearningImport,
      termNumber,
    ]
  );
  const handleExportCsvFile = React.useCallback(() => {
    try {
      const csv = createProgramLearningContentCsv(alignedContentRows);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const gradeLabel = classParsed?.title ?? 'class';
      const scopeLabel = (project?.name ?? '프로젝트').trim() || '프로젝트';
      const termLabel = termNumber ? `${termNumber}학기` : '학기';
      const fileName = `${gradeLabel}_${scopeLabel}_${termLabel}_학습내용.csv`.replace(/[\\/:*?"<>|]/g, '_');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSaveToastState({
        kind: 'success',
        message: 'CSV 파일로 내보냈습니다.',
      });
    } catch (error) {
      setSaveToastState({
        kind: 'error',
        message: error instanceof Error ? error.message : 'CSV 파일 내보내기에 실패했습니다.',
      });
    }
  }, [alignedContentRows, classParsed?.title, project?.name, termNumber]);
  const handleImportProjectTimetableFromAnnual = React.useCallback(async (): Promise<string[]> => {
    if (!workspaceId || !effectiveGradeId || !termNumber || !project) return [];
    const projectColorNorm = normalizeHexColorForProjectCompare(project.colorHex);
    if (!projectColorNorm) return [];
    const annualRows = await getAnnualTimetableCells(workspaceId, academicYear, termNumber, [effectiveGradeId]);
    const annualMap = annualRows.reduce<Record<string, string>>((acc, row) => {
      if (row.is_base_excluded) return acc;
      if (normalizeHexColorForProjectCompare(row.color_hex) !== projectColorNorm) return acc;
      const key = buildAnnualCellKey(row.grade_id, row.monday_iso, row.weekday_key, row.slot_index);
      acc[key] = row.text_value ?? '';
      return acc;
    }, {});
    return rows.map((r) => annualMap[r.annualCellKey] ?? '');
  }, [academicYear, effectiveGradeId, project, rows, workspaceId, termNumber]);
  const handleExportProjectTimetableToAnnual = React.useCallback(
    async (tableTimetableTexts: string[]): Promise<void> => {
      if (!workspaceId || !effectiveGradeId || !termNumber || !project) return;
      const projectColorNorm = normalizeHexColorForProjectCompare(project.colorHex);
      if (!projectColorNorm) return;
      const annualRows = await getAnnualTimetableCells(workspaceId, academicYear, termNumber, [effectiveGradeId]);
      const annualByKey = annualRows.reduce<Record<string, (typeof annualRows)[number]>>((acc, row) => {
        const key = buildAnnualCellKey(row.grade_id, row.monday_iso, row.weekday_key, row.slot_index);
        acc[key] = row;
        return acc;
      }, {});
      const targetAnnualKeys = rows
        .map((r) => r.annualCellKey)
        .filter((key) => {
          const cell = annualByKey[key];
          if (!cell) return false;
          if (cell.is_base_excluded) return false;
          return normalizeHexColorForProjectCompare(cell.color_hex) === projectColorNorm;
        });
      if (!targetAnnualKeys.length) return;
      /**
       * 일정 슬롯 전부에 맞춤. 표 행이 짧거나 시간표 셀이 빈칸이면 ''로 내보내
       * 연간 셀을 비운다(행 전체 삭제 후 빈칸 반영).
       */
      const upserts = targetAnnualKeys
        .map((key, idx) => {
          const cell = annualByKey[key];
          if (!cell) return null;
          const nextText = (tableTimetableTexts[idx] ?? '').trim();
          if ((cell.text_value ?? '') === nextText) return null;
          return {
            id: cell.id,
            workspace_id: cell.workspace_id,
            grade_id: cell.grade_id,
            academic_year: cell.academic_year,
            term_number: cell.term_number,
            monday_iso: cell.monday_iso,
            weekday_key: cell.weekday_key,
            slot_index: cell.slot_index,
            text_value: nextText,
            memo: cell.memo ?? null,
            color_hex: cell.color_hex,
            slot_kind: cell.slot_kind,
            is_editable: cell.is_editable,
            is_dedicated: cell.is_dedicated,
            is_base_excluded: cell.is_base_excluded,
          };
        })
        .filter((row): row is NonNullable<typeof row> => row != null);
      if (!upserts.length) return;
      await upsertAnnualTimetableCells(upserts);
      emitAnnualTimetableSaved({
        workspaceId,
        academicYear,
        termNumbers: [termNumber],
        gradeIds: [effectiveGradeId],
      });
    },
    [academicYear, effectiveGradeId, project, rows, workspaceId, termNumber]
  );
  const [saveAction, setSaveAction] = React.useState<(() => Promise<void>) | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [saveToastState, setSaveToastState] = React.useState<
    | {
        kind: 'saving' | 'success' | 'error';
        message: string;
      }
    | null
  >(null);
  const [slideDraftDirtyRev, setSlideDraftDirtyRev] = React.useState(0);
  const [worksheetDraftDirtyRev, setWorksheetDraftDirtyRev] = React.useState(0);
  const handleBindSaveAction = React.useCallback((fn: (() => Promise<void>) | null) => {
    setSaveAction(() => fn ?? null);
  }, []);
  const { setProgramProgressDirty, saveProgramProgressRef } = React.useContext(GradeTrackLeaveContext);
  React.useEffect(() => {
    const refresh = () => setWorkspaceStateRev((v) => v + 1);
    window.addEventListener(PROGRAM_WORKSPACE_CHANGED_EVENT, refresh as EventListener);
    return () => window.removeEventListener(PROGRAM_WORKSPACE_CHANGED_EVENT, refresh as EventListener);
  }, []);
  React.useEffect(() => {
    if (!activeWorkspaceScopeId) return;
    pruneProgramWorkspaceScopes([activeWorkspaceScopeId]);
  }, [activeWorkspaceScopeId]);
  React.useEffect(() => {
    if (!projectScopeId || scopedContentRows.length === 0) return;
    const ent = getProgramWorkspaceScopeEntry(projectScopeId);
    if (!ent?.isDirty || ent.persistKind !== 'replace' || !ent.rows?.length) return;
    if (ent.rows.length >= scopedContentRows.length) return;
    if (!isPristineMinimumProgramContentPlaceholderRows(ent.rows)) return;
    removeProgramWorkspaceScopeEntry(projectScopeId);
  }, [projectScopeId, scopedContentRows.length]);
  React.useEffect(() => {
    if (!activeWorkspaceScopeId) return () => {};
    return registerDiscardDirtyWorkspaceOnPageHide(activeWorkspaceScopeId);
  }, [activeWorkspaceScopeId]);
  const handleWorkspaceRowsChange = React.useCallback(
    (rowsFromTable: ProgramFixedContentRowUpsert[] | null, meta?: ProgramWorkspaceRowsChangeMeta) => {
      if (!effectiveGradeId || !termNumber || !scopeKey) return;
      if (rowsFromTable == null) {
        removeProgramWorkspaceScopeEntry(projectScopeId);
        return;
      }
      if (isPristineMinimumProgramContentPlaceholderRows(rowsFromTable)) {
        removeProgramWorkspaceScopeEntry(projectScopeId);
        return;
      }
      const baseline = persistedAlignedWorkspaceUpserts;
      const dirty =
        baseline.length !== rowsFromTable.length ||
        programFixedWorkspaceRowsDigest(rowsFromTable) !== programFixedWorkspaceRowsDigest(baseline);
      upsertProgramWorkspaceScopeEntry({
        scopeId: projectScopeId,
        scopeType: 'project',
        scopeKey,
        workspaceId,
        gradeId: effectiveGradeId,
        academicYear,
        termNumber,
        rows: rowsFromTable,
        isDirty: dirty,
        updatedAt: Date.now(),
        persistKind: resolveProgramWorkspacePersistKind(meta, rowsFromTable.length, baseline.length),
      });
    },
    [
      academicYear,
      persistedAlignedWorkspaceUpserts,
      effectiveGradeId,
      projectScopeId,
      workspaceId,
      scopeKey,
      termNumber,
    ]
  );
  const handleEnsureWorkspaceSnapshot = React.useCallback(
    (rowsFromTable: ProgramFixedContentRowUpsert[]) => {
      if (!effectiveGradeId || !termNumber || !scopeKey || !rowsFromTable.length) return;
      /** 일정·스코프 행 0일 때 UI용 가짜 1행 — LS 스냅샷으로 굳히지 않음 */
      if (isPristineMinimumProgramContentPlaceholderRows(rowsFromTable)) return;
      ensureProgramWorkspaceScopeSnapshot({
        scopeId: projectScopeId,
        scopeType: 'project',
        scopeKey,
        workspaceId,
        gradeId: effectiveGradeId,
        academicYear,
        termNumber,
        rows: rowsFromTable,
        isDirty: false,
        updatedAt: Date.now(),
      });
    },
    [academicYear, effectiveGradeId, projectScopeId, workspaceId, scopeKey, termNumber]
  );
  React.useEffect(() => {
    if (!saveToastState || saveToastState.kind === 'saving') return;
    const t = window.setTimeout(() => setSaveToastState(null), 1800);
    return () => window.clearTimeout(t);
  }, [saveToastState]);
  const handleSave = React.useCallback(async () => {
    setSaveToastState({ kind: 'saving', message: '저장 중입니다...' });
    beginHold(cloneProgramTableHoldPayload({ tableRows: displayTableRows, programCellsByAnnualKey: displayProgramCellsByAnnualKey }));
    try {
      const workspaceDirty = isProgramWorkspaceScopeDirty(projectScopeId);
      const shouldRunTableSave = !!(saveAction && (hasUnsavedChanges || workspaceDirty));
      if (shouldRunTableSave) await saveAction!();
      let uiRefreshMode: 'full' | 'cache' = 'full';
      if (effectiveGradeId && termNumber) {
        const result = await saveProgramGradeTermWorkspaceAndAttachments(
          {
            workspaceId,
            gradeId: effectiveGradeId,
            academicYear,
            termNumber,
          },
          {
            /** saveAction이 이미 프로젝트 스코프를 저장했을 때만 통합 저장에서 제외 */
            excludeWorkspaceScopeId: shouldRunTableSave ? projectScopeId : undefined,
          }
        );
        uiRefreshMode = result.uiRefreshMode;
      }
      setHasUnsavedChanges(false);
      setSlideDraftDirtyRev((v) => v + 1);
      setWorksheetDraftDirtyRev((v) => v + 1);
      setWorkspaceStateRev((v) => v + 1);
      if (uiRefreshMode === 'full') {
        setSaveRefreshRev((v) => v + 1);
      }
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
      endHold();
      setSaveToastState({ kind: 'success', message: '저장되었습니다.' });
    } catch (e) {
      endHold();
      setSaveToastState({
        kind: 'error',
        message: e instanceof Error ? e.message : '저장에 실패했습니다.',
      });
    }
  }, [
    academicYear,
    beginHold,
    displayProgramCellsByAnnualKey,
    displayTableRows,
    effectiveGradeId,
    endHold,
    hasUnsavedChanges,
    projectScopeId,
    saveAction,
    workspaceId,
    termNumber,
  ]);

  const handleDiscardUnsaved = React.useCallback(() => {
    discardAllProgramFamilyLeaveDrafts();
    endHold();
    setProgramTableMountKey((k) => k + 1);
    setSaveRefreshRev((v) => v + 1);
    setWorkspaceStateRev((v) => v + 1);
    setSlideDraftDirtyRev((v) => v + 1);
    setWorksheetDraftDirtyRev((v) => v + 1);
    setHasUnsavedChanges(false);
  }, [endHold]);

  const pageWorkspaceDirty = isProgramWorkspaceScopeDirty(projectScopeId);
  const projectScopeServerSync = useProgramWorkspaceScopeServerSync({
    scopeId: projectScopeId,
    enabled: dataActive && !!projectScopeId && !!effectiveGradeId && !!termNumber && !!scopeKey,
    serverRows: scopedContentRows,
    fetchHead: React.useCallback(
      () =>
        fetchProgramScopedContentRowsHead(
          workspaceId,
          academicYear,
          termNumber as 1 | 2,
          effectiveGradeId,
          'project',
          scopeKey
        ),
      [academicYear, effectiveGradeId, scopeKey, termNumber, workspaceId]
    ),
    onAutoRefresh: React.useCallback(() => setSaveRefreshRev((v) => v + 1), []),
  });
  const programGradeTermSaveTarget = React.useMemo(
    () => ({
      workspaceId,
      gradeId: effectiveGradeId,
      academicYear,
      termNumber: (termNumber ?? 1) as 1 | 2,
    }),
    [academicYear, effectiveGradeId, termNumber, workspaceId]
  );
  const leavePending = pageWorkspaceDirty || (hasUnsavedChanges && saveAction != null);
  const programSavePending = React.useMemo(() => {
    void workspaceStateRev;
    void slideDraftDirtyRev;
    void worksheetDraftDirtyRev;
    if (!effectiveGradeId || !termNumber) return false;
    return computeProgramSavePending({
      target: programGradeTermSaveTarget,
      hasUnsavedTableChanges: hasUnsavedChanges,
      hasTableSaveAction: !!saveAction,
    });
  }, [
    effectiveGradeId,
    hasUnsavedChanges,
    programGradeTermSaveTarget,
    saveAction,
    slideDraftDirtyRev,
    termNumber,
    worksheetDraftDirtyRev,
    workspaceStateRev,
  ]);
  const programLeaveDirty = pageWorkspaceDirty || leavePending;

  React.useEffect(() => {
    const syncSlides = () => setSlideDraftDirtyRev((v) => v + 1);
    const syncWorksheets = () => setWorksheetDraftDirtyRev((v) => v + 1);
    const onStorage = (e: StorageEvent) => {
      if (!e.key) return;
      if (e.key.startsWith(PROGRAM_CONTENT_SLIDE_DRAFT_PREFIX)) syncSlides();
      if (e.key.startsWith(LEARNING_WORKSHEET_PENDING_PREFIX)) syncWorksheets();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT, syncSlides);
    window.addEventListener(LEARNING_WORKSHEET_PENDING_CHANGED_EVENT, syncWorksheets);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT, syncSlides);
      window.removeEventListener(LEARNING_WORKSHEET_PENDING_CHANGED_EVENT, syncWorksheets);
    };
  }, []);
  React.useEffect(() => {
    if (!isActive) return;
    setProgramProgressDirty(programLeaveDirty);
  }, [isActive, programLeaveDirty, setProgramProgressDirty]);
  React.useEffect(() => {
    if (!isActive) return undefined;
    saveProgramProgressRef.current = { save: handleSave, discard: handleDiscardUnsaved };
    return () => {
      saveProgramProgressRef.current = null;
    };
  }, [handleDiscardUnsaved, handleSave, isActive, saveProgramProgressRef]);

  if (!projectIdParam) {
    return (
      <section>
        <h2>{titlePrefix}프로젝트 프로그램</h2>
        {!effectiveGradeId ? (
          <p className="page-desc">
            연간진도표에서 클래스를 선택하거나, 사이드바 상단에 클래스가 표시된 뒤 이 메뉴로 이동해 주세요.
          </p>
        ) : (
          <p className="page-desc">
            왼쪽 <strong>프로젝트 프로그램</strong>에서 프로젝트를 선택하면 이 화면에서 해당 프로젝트 정보를 볼 수 있습니다.
          </p>
        )}
      </section>
    );
  }

  const emptyProgramTableCard = (
    <ProgramProgressEmptyStateTableCard
      mode="project"
      toolbarVariant="project"
      academicYear={academicYear}
      importProps={{
        workspaceId,
        gradeId: effectiveGradeId,
        defaultGradeNumber: defaultGradeNumberForImport,
        termNumber: termNumber ?? 1,
        preferredTemplateName: exportLearningTemplateName,
        modalOpen: importLearningModalOpen,
        onCloseModal: () => {
          if (!importLearningApplying) setImportLearningModalOpen(false);
        },
        onOpenImport: () => setImportLearningModalOpen(true),
        applying: importLearningApplying,
        onApply: handleApplyProgramLearningImport,
        importBlocked: programImportLearningBlocked,
      }}
    />
  );

  return (
    <section>
      <div className="cp-program-page-header">
        <h2>
          {titlePrefix}
          {project ? project.name : '프로젝트 프로그램'}
          {termNumber ? (
            <>
              {' '}
              <span className="cp-program-page-term-pill">{termNumber}학기</span>
            </>
          ) : null}
        </h2>
        <div className="cp-program-page-save-actions">
          <ProgramToolbarUndoRedoButtons
            canUndo={programUndoRedoAvail.canUndo}
            canRedo={programUndoRedoAvail.canRedo}
            onUndo={() => {
              programUndoRedoApiRef.current?.undo();
            }}
            onRedo={() => {
              programUndoRedoApiRef.current?.redo();
            }}
          />
          <button
            type="button"
            className="cp-btn-save-all"
            onClick={handleSave}
            disabled={!programSavePending}
          >
            {programSavePending ? '프로그램 통합 저장하기' : '프로그램 변경사항 없음'}
          </button>
        </div>
      </div>
      {!effectiveGradeId ? (
        <>
          <p className="page-desc">
            연간진도표에서 클래스를 선택하거나, 사이드바에서 프로젝트 링크로 다시 들어와 주세요.
          </p>
          {emptyProgramTableCard}
        </>
      ) : !termNumber ? (
        <>
          <p className="page-desc">왼쪽 프로젝트 프로그램에서 1학기 또는 2학기 프로젝트를 선택해 주세요.</p>
          {emptyProgramTableCard}
        </>
      ) : !project ? (
        <>
          <p className="page-desc">
            선택한 클래스·학사일정 학년도에 해당 프로젝트가 없습니다. 연간시간표에서 프로젝트를 등록했는지 확인해 주세요.
          </p>
          {emptyProgramTableCard}
        </>
      ) : error ? (
        <p className="page-desc" role="alert">{error}</p>
      ) : showTableLoading ? (
        <p className="page-desc">불러오는 중…</p>
      ) : !selectedEntry ? (
        <>{emptyProgramTableCard}</>
      ) : !termDef ? (
        <>
          <p className="page-desc">해당 학기의 학사일정이 없습니다.</p>
          {emptyProgramTableCard}
        </>
      ) : (
        <>
          {rows.length === 0 ? (
            <p className="page-desc">
              연간 시간표에 이 프로젝트 색으로 입력된 일정이 아직 없습니다. 프로그램 내용은 미리 입력할 수 있으며,
              연간에 일정이 생기면 같은 순서의 행과 맞춰집니다.
            </p>
          ) : null}
          <ProgramWorkspaceStaleConflictBanner
            visible={projectScopeServerSync.staleConflict}
            onAcceptServer={projectScopeServerSync.acceptServerRefresh}
            onDismiss={projectScopeServerSync.dismissStaleConflict}
          />
          <article className="cp-card cp-weekly-class-hours-card cp-program-progress-page-table-card">
            <div className="cp-weekly-class-hours-controls cp-program-progress-table-card-toolbar">
              <div className="cp-program-progress-table-card-toolbar-left">
                <label>
                  학년도
                  <span
                    className="cp-academic-year-readonly"
                    aria-label={`학년도 ${academicYear}학년도(학사일정 기준 고정)`}
                  >
                    {academicYear}학년도
                  </span>
                </label>
              </div>
              <div className="cp-program-progress-toolbar-trailing-actions">
              <div className="cp-program-progress-toolbar-group" role="group" aria-label="프로젝트 시간표 가져오기/내보내기">
                <button
                  type="button"
                  className="cp-btn-secondary cp-program-import-learning-toolbar-btn"
                  disabled={
                    !projectTimetableSyncReady ||
                    projectTimetableSyncBusy ||
                    importLearningApplying ||
                    exportLearningApplying ||
                    displayTableRows.length === 0
                  }
                  title="연간시간표의 프로젝트 시간표 가져오기"
                  onClick={() => setProjectTimetableSyncConfirm('import')}
                >
                  <svg
                    className="cp-project-timetable-sync-toolbar-icon"
                    width={14}
                    height={14}
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                  >
                    <path
                      d="M12 5V19"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16 15L12 19"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8 15L12 19"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>연간시간표의 프로젝트 시간표 가져오기</span>
                </button>
                <button
                  type="button"
                  className="cp-btn-secondary cp-program-import-learning-toolbar-btn"
                  disabled={
                    !projectTimetableSyncReady ||
                    projectTimetableSyncBusy ||
                    importLearningApplying ||
                    exportLearningApplying ||
                    displayTableRows.length === 0
                  }
                  title="프로젝트 시간표 연간시간표에 내보내기"
                  onClick={() => setProjectTimetableSyncConfirm('export')}
                >
                  <svg
                    className="cp-project-timetable-sync-toolbar-icon"
                    width={14}
                    height={14}
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                  >
                    <path
                      d="M12 5V19"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16 9L12 5"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8 9L12 5"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>프로젝트 시간표 연간시간표에 내보내기</span>
                </button>
              </div>
              <span className="cp-program-progress-toolbar-vdivider" aria-hidden />
              <div className="cp-program-progress-toolbar-group" role="group" aria-label="학습내용 가져오기/내보내기">
                <ProgramImportLearningDisabledHint
                  hint={resolveProgramImportLearningDisabledHint({
                    contentBlocked: programImportLearningBlocked,
                  })}
                >
                <button
                  type="button"
                  className="cp-btn-secondary cp-program-import-learning-toolbar-btn"
                  disabled={programImportLearningBlocked || importLearningApplying || exportLearningApplying}
                  title={programImportLearningBlocked ? undefined : '가져오기 방식을 선택합니다.'}
                  onClick={() => setImportMethodSelectOpen(true)}
                >
                  학습내용 가져오기
                </button>
                </ProgramImportLearningDisabledHint>
                <button
                  type="button"
                  className="cp-btn-secondary cp-program-import-learning-toolbar-btn"
                  disabled={importLearningApplying || exportLearningApplying}
                  title="내보내기 방식을 선택합니다."
                  onClick={() => setExportMethodSelectOpen(true)}
                >
                  학습내용 내보내기
                </button>
              </div>
                <ProgramTableDocumentExportButtons
                  onSaveExcel={handleProgramDocumentSaveExcel}
                  onSavePdf={handleProgramDocumentSavePdf}
                  onPrint={handleProgramDocumentPrint}
                  disabled={!canExportProgramDocument}
                  busy={programDocumentExportBusy || importLearningApplying || exportLearningApplying}
                />
              </div>
            </div>
            {projectTimetableSyncConfirm
              ? createPortal(
                  <div
                    className="cp-modal-backdrop"
                    role="presentation"
                    onMouseDown={(e) => {
                      if (e.target === e.currentTarget && !projectTimetableSyncBusy) {
                        setProjectTimetableSyncConfirm(null);
                      }
                    }}
                  >
                    <div
                      className="cp-modal cp-leave-modal-box"
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="cp-project-timetable-sync-confirm-title"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <h3 id="cp-project-timetable-sync-confirm-title" className="cp-leave-modal-title">
                        {projectTimetableSyncConfirm === 'import'
                          ? '연간시간표의 프로젝트 시간표 가져오기'
                          : '프로젝트 시간표 연간시간표에 내보내기'}
                      </h3>
                      <p className="cp-leave-modal-desc">
                        {projectTimetableSyncConfirm === 'import'
                          ? '연간시간표의 프로젝트 칸 시간표명을 이 표로 가져옵니다. 표에 이미 입력된 시간표명은 덮어씁니다. 계속할까요?'
                          : '이 표의 시간표명을 연간시간표의 해당 프로젝트 칸에 내보냅니다. 연간시간표 셀 텍스트가 덮어씌워집니다. 계속할까요?'}
                      </p>
                      <div className="cp-modal-footer cp-leave-modal-actions">
                        <button
                          type="button"
                          className="cp-btn-secondary"
                          disabled={projectTimetableSyncBusy}
                          onClick={() => setProjectTimetableSyncConfirm(null)}
                        >
                          취소
                        </button>
                        <button
                          type="button"
                          className="cp-btn-primary"
                          disabled={projectTimetableSyncBusy}
                          onClick={() => {
                            const action = projectTimetableSyncConfirm;
                            setProjectTimetableSyncConfirm(null);
                            if (action === 'import') {
                              projectTimetableSyncApiRef.current?.importFromAnnual();
                            } else if (action === 'export') {
                              projectTimetableSyncApiRef.current?.exportToAnnual();
                            }
                          }}
                        >
                          확인
                        </button>
                      </div>
                    </div>
                  </div>,
                  document.body
                )
              : null}
            <ProgramLearningContentActionSelectModal
              open={importMethodSelectOpen}
              mode="import"
              busy={importLearningApplying || exportLearningApplying}
              onClose={() => setImportMethodSelectOpen(false)}
              onSelectCsv={() => {
                setImportMethodSelectOpen(false);
                importCsvInputRef.current?.click();
              }}
              onSelectWorkspace={() => {
                setImportMethodSelectOpen(false);
                setImportLearningModalOpen(true);
              }}
            />
            <ProgramLearningContentActionSelectModal
              open={exportMethodSelectOpen}
              mode="export"
              busy={importLearningApplying || exportLearningApplying}
              onClose={() => setExportMethodSelectOpen(false)}
              onSelectCsv={() => {
                setExportMethodSelectOpen(false);
                handleExportCsvFile();
              }}
              onSelectWorkspace={() => {
                setExportMethodSelectOpen(false);
                setExportLearningModalOpen(true);
              }}
            />
            <input
              ref={importCsvInputRef}
              type="file"
              accept=".csv,text/csv,.tsv,text/tab-separated-values"
              style={{ display: 'none' }}
              onChange={(event) => {
                void handleImportCsvFileSelected(event);
              }}
            />
            <ProgramExportLearningContentModal
              open={exportLearningModalOpen}
              onClose={() => {
                if (!exportLearningApplying) setExportLearningModalOpen(false);
              }}
              applying={exportLearningApplying}
              gradeNumber={defaultGradeNumberForImport}
              termNumber={termNumber ?? 1}
              templateName={exportLearningTemplateName}
              onConfirm={handleExportProgramLearning}
            />
            <ProgramImportLearningContentModal
              open={importLearningModalOpen}
              onClose={() => {
                if (!importLearningApplying) setImportLearningModalOpen(false);
              }}
              workspaceId={workspaceId}
              gradeId={effectiveGradeId}
              defaultGradeNumber={defaultGradeNumberForImport}
              termNumber={termNumber ?? 1}
              preferredTemplateName={exportLearningTemplateName}
              applying={importLearningApplying}
              onApply={handleApplyProgramLearningImport}
            />
            <ProgramProgressTable
              key={`${projectScopeId}::${programTableMountKey}`}
              rows={displayTableRows}
              mode="project_to_template"
              workspaceId={workspaceId}
              academicYear={academicYear}
              termNumber={termNumber}
              focusAnnualCellKey={focusAnnualCellKeyFromUrl || null}
              programCellsByAnnualKey={displayProgramCellsByAnnualKey}
              saveMode="fixed_content"
              onUpsertFixedContentRows={upsertScopeContentRows}
              onBindSaveAction={handleBindSaveAction}
              onDirtyChange={setHasUnsavedChanges}
              workspaceScopeId={projectScopeId}
              onWorkspaceRowsChange={handleWorkspaceRowsChange}
              onEnsureWorkspaceSnapshot={handleEnsureWorkspaceSnapshot}
              classScheduleSectionRows={selectedEntry?.sectionRows ?? []}
              registeredTimetableNames={registeredTimetableNames}
              registeredTimetableWildcardChars={registeredTimetableWildcardChars}
              onImportProjectTimetableFromAnnual={handleImportProjectTimetableFromAnnual}
              onExportProjectTimetableToAnnual={handleExportProjectTimetableToAnnual}
              onBindProjectTimetableSyncActions={handleBindProjectTimetableSyncActions}
              scheduleSlotAnnualCellKeys={scheduleSlotAnnualCellKeys}
              programUndoRedoApiRef={programUndoRedoApiRef}
              onProgramUndoRedoAvailabilityChange={setProgramUndoRedoAvail}
              programDataCleanupGradeId={effectiveGradeId}
              programDataCleanupScopeType="project"
              programDataCleanupScopeKey={scopeKey}
              onProgramDataCleanupComplete={() => setSaveRefreshRev((v) => v + 1)}
            />
          </article>
        </>
      )}
      <button
        type="button"
        className="cp-fab-save"
        onClick={() => {
          void handleSave();
        }}
        disabled={!programSavePending}
        aria-label={programSavePending ? '프로그램 통합 저장하기' : '프로그램 변경사항 없음'}
        title={programSavePending ? '프로그램 통합 저장하기' : '프로그램 변경사항 없음'}
      >
        <img src="/Save.svg" alt="" className="cp-fab-save-icon" />
      </button>
      {saveToastState ? (
        <div className="cp-toast-auto" role="status" aria-live="polite">
          <div className={`cp-toast-auto-box cp-toast-auto-box-${saveToastState.kind}`}>
            {saveToastState.kind === 'saving' ? (
              <span className="cp-toast-auto-spinner" aria-hidden="true" />
            ) : saveToastState.kind === 'success' ? (
              <span className="cp-toast-auto-success-icon" aria-hidden="true">
                v
              </span>
            ) : null}
            <span>{saveToastState.message}</span>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ProgramProjectPage;
